/* eslint-disable react/no-multi-comp */
import { useEffect } from 'react';
import Link from 'next/link';
import { useParams, usePathname } from 'next/navigation';
import { useRouter } from 'next/router';

import clsx from 'clsx';
import { matchSorter } from 'match-sorter';

import useAcl from '@/hooks/user/useAcl';
import useActiveSocialProfile from '@/hooks/user/useActiveSocialProfile';
import { useMenuEntryAccess } from '@/utils/hooks/menu/useMenuEntryAccess';

import { formatUrlPathname, formatUrls } from '@/utils/urls/formatUrl';

import type {
    MenuEntryDropdownType,
    MenuEntryType,
    SubMenuEntryType
} from '@/lib/platform/menu/menu';

import Icon from '@/components/base/Icon';
import { MenuNotification } from '@/components/layouts/LayoutPlatform/components/Menu/components/MenuNotification';
import { TooltipPlanRestriction } from '@/components/layouts/LayoutPlatform/components/Menu/components/TooltipPlanRestriction';
import { TransitionWrapper } from '@/components/layouts/LayoutPlatform/components/Menu/components/TransitionWrapper';
export function formatEntryLinks(entry: SubMenuEntryType, params: ReturnType<typeof useParams>) {
    const formattedUrlActiveMenu =
        'urlActiveMenu' in entry ? formatUrls(entry.urlActiveMenu, params) : [];
    const formattedLink = formatUrlPathname(entry.link, params);

    return { formattedLink, formattedUrlActiveMenu };
}

type MenuEntryProps = {
    activeEntry: string;
    isMenuOpen: boolean;
    isMenuMobile?: boolean;
    stickyMenu?: boolean;
    entry: MenuEntryType;
    setActiveEntry: (arg: MenuEntryType['id']) => void;
};

export const MenuEntry = ({
    entry,
    isMenuOpen,
    activeEntry,
    stickyMenu,
    setActiveEntry,
    isMenuMobile = false
}: MenuEntryProps) => {
    const { hasAcl } = useAcl();
    const pathname = usePathname();
    const params = useParams();

    if (
        ('link' in entry && !('displaySubMenuMobile' in entry)) ||
        ('link' in entry && 'displaySubMenuMobile' in entry && !isMenuMobile)
    ) {
        const { formattedUrlActiveMenu, formattedLink } = formatEntryLinks(entry, params);

        const isActive = pathname === formattedLink || formattedUrlActiveMenu.includes(pathname);

        const isDisabled =
            entry?.acl?.permissions_plan_restriction &&
            !hasAcl(entry.acl.permissions_plan_restriction);

        return (
            <TooltipPlanRestriction disabled={!isDisabled}>
                <div>
                    <Link
                        href={entry.link}
                        onClick={
                            entry.onClick !== undefined
                                ? (e) => entry.onClick(e, hasAcl)
                                : undefined
                        }
                        key={entry.name}
                        className={clsx(
                            'group relative flex w-full items-center whitespace-nowrap rounded-lg p-1 group-hover:text-icoBlue',
                            {
                                'bg-icoBlue/10 text-icoBlue': isActive,
                                'hover:bg-icoBlue/10 hover:text-icoBlue': !isActive,
                                'pointer-events-none cursor-not-allowed': isDisabled
                            }
                        )}>
                        <TextContent
                            icon={entry.icon}
                            isActive={isActive}
                            activeEntry={activeEntry}
                            name={entry.name}
                            disabled={isDisabled}
                            isMenuOpen={isMenuOpen}
                            id={entry.id}
                        />
                    </Link>
                </div>
            </TooltipPlanRestriction>
        );
    }

    return (
        <MenuEntryDropdown
            entry={entry}
            stickyMenu={stickyMenu}
            isMenuOpen={isMenuOpen}
            isMenuMobile={isMenuMobile}
            activeEntry={activeEntry}
            setActiveEntry={setActiveEntry}
        />
    );
};

type MenuEntryDropdownProps = Omit<MenuEntryProps, 'entry'> & {
    entry: MenuEntryDropdownType;
};

function MenuEntryDropdown({
    entry,
    activeEntry,
    stickyMenu,
    isMenuOpen,
    isMenuMobile,
    setActiveEntry
}: MenuEntryDropdownProps) {
    const pathname = usePathname();
    const params = useParams();
    const router = useRouter();
    const { activeSocialProfile } = useActiveSocialProfile();

    const { checkAccess } = useMenuEntryAccess();

    // Helper function to get first sub menu item and check network access
    const getFirstSubMenuItemWithAccess = () => {
        const firstSubMenuItem =
            'subMenuItems' in entry && entry.subMenuItems.length > 0 ? entry.subMenuItems[0] : null;

        const hasNetworkAccess =
            firstSubMenuItem && activeSocialProfile?.social_network_type
                ? checkAccess(activeSocialProfile?.social_network_type, firstSubMenuItem.link)
                : false;

        return { firstSubMenuItem, hasNetworkAccess };
    };

    let formattedLinks: ReturnType<typeof formatEntryLinks>[] = [];
    if ('subMenuItems' in entry) {
        formattedLinks = entry.subMenuItems.map((subEntry) => {
            return formatEntryLinks(subEntry, params);
        });
    }

    if ('displaySubMenuMobile' in entry) {
        formattedLinks = [formatEntryLinks(entry, params)];
    }

    const isSubMenuActivated = Boolean(
        matchSorter(formattedLinks, pathname, {
            keys: [
                { threshold: matchSorter.rankings.EQUAL, key: 'formattedLink' },
                { threshold: matchSorter.rankings.EQUAL, key: 'formattedUrlActiveMenu' }
            ]
        }).length
    );

    const isActive = activeEntry === entry.id || isSubMenuActivated;

    useEffect(() => {
        if (isActive && !isMenuMobile && stickyMenu) {
            setActiveEntry(entry.id);
        }
    }, []);

    // Monitor social profile changes and close menu if no access
    useEffect(() => {
        if ('subMenuItems' in entry && entry.subMenuItems.length > 0) {
            const { hasNetworkAccess } = getFirstSubMenuItemWithAccess();

            if (!hasNetworkAccess && activeEntry === entry.id) {
                // Profile has no rights and menu is open, close it
                setActiveEntry('');
            }
        }
    }, [activeSocialProfile?.social_network_type, entry, activeEntry, params, setActiveEntry]);

    const handleMenuClick = () => {
        // Get the first available sub-route
        const { firstSubMenuItem, hasNetworkAccess } = getFirstSubMenuItemWithAccess();
        if (!firstSubMenuItem) {
            // If no sub-menu, default behavior
            setActiveEntry(activeEntry === entry.id ? '' : entry.id);
            return;
        }

        // Check if we are on a sub-menu route
        const isOnSubMenuRoute = formattedLinks.some(
            (link) =>
                pathname === link.formattedLink || link.formattedUrlActiveMenu.includes(pathname)
        );

        if (isOnSubMenuRoute) {
            if (hasNetworkAccess) {
                // If on sub-menu route AND has access, toggle the menu
                setActiveEntry(activeEntry === entry.id ? '' : entry.id);
            } else {
                // If on sub-menu route BUT no access, close the menu
                setActiveEntry('');
            }
        } else {
            if (!hasNetworkAccess) {
                // If not on sub-menu route, and no access, ALWAYS redirect to show upsell page
                router.push(firstSubMenuItem.link);
                setActiveEntry('');
            }
            if (hasNetworkAccess) {
                // If has access, also open the menu
                setActiveEntry(entry.id);
            }
        }
    };

    const getChevronIcon = () => {
        const { hasNetworkAccess } = getFirstSubMenuItemWithAccess();

        if (!hasNetworkAccess) {
            return '';
        }

        if (isMenuMobile) {
            return 'chevron-right';
        }
        return activeEntry === entry.id ? 'chevron-top' : 'chevron-bottom';
    };

    return (
        <button
            className={clsx(
                'group relative flex w-full items-center justify-between overflow-hidden rounded-lg border-none p-1 shadow-none focus:ring-0 focus:ring-offset-0',
                {
                    'bg-icoBlue/10 text-icoBlue': isActive,
                    'hover:bg-icoBlue/10 hover:text-icoBlue': !isActive
                }
            )}
            data-testid={`button-menu-entry-${entry.id}${isActive ? '-active' : ''}`}
            onClick={handleMenuClick}>
            <span className="flex items-center whitespace-nowrap group-hover:text-icoBlue">
                <TextContent
                    icon={entry.icon}
                    isActive={isActive}
                    activeEntry={activeEntry}
                    name={entry.name}
                    isMenuOpen={isMenuOpen}
                    id={entry.id}
                />
            </span>
            {isMenuOpen ? (
                <Icon
                    icon={getChevronIcon()}
                    className={clsx('text-xs group-hover:text-icoBlue', {
                        'text-icoBlue': isActive,
                        'text-icoGray-400 dark:text-icoDarkMode-wolf': !isActive
                    })}
                />
            ) : null}
        </button>
    );
}

export type MenuIconProps = Pick<TextContentProps, 'icon' | 'isActive' | 'disabled' | 'id'>;

export function MenuIcon({ icon, isActive, disabled, id }: MenuIconProps) {
    return (
        <div className="relative flex items-center">
            <Icon
                icon={icon}
                data-testid={`icon-${icon}`}
                className={clsx(
                    'align-middle text-base text-icoGray-500 group-hover:text-icoBlue dark:text-icoDarkMode-wolf',
                    {
                        'group-hover:hidden': !isActive,
                        'opacity-40': disabled,
                        hidden: isActive
                    }
                )}
            />
            <Icon
                icon={`${icon}-on`}
                className={clsx('align-middle text-base text-icoBlue', {
                    'hidden group-hover:inline-block': !isActive
                })}
            />
            {id === 'notifications' && !disabled ? <MenuNotification /> : null}
        </div>
    );
}

type TextContentProps = Pick<MenuEntryType, 'icon' | 'name' | 'id'> &
    Pick<MenuEntryProps, 'activeEntry' | 'isMenuOpen'> & {
        isActive: boolean;
        disabled?: boolean;
    };

function TextContent({
    icon,
    isActive,
    activeEntry,
    disabled,
    name,
    isMenuOpen,
    id
}: TextContentProps) {
    return (
        <>
            <MenuIcon disabled={disabled} icon={icon} id={id} isActive={isActive} />
            <TransitionWrapper
                className={clsx('ml-2 flex items-center gap-1 font-GMedium text-xs', {
                    'text-icoBlue': isActive || activeEntry === id,
                    'text-icoGray-500 group-hover:text-icoBlue dark:text-icoDarkMode-wolf':
                        !isActive && activeEntry !== id && !disabled,
                    'text-icoGray-500/40 dark:text-icoDarkMode-wolf/40': disabled
                })}
                isOpen={isMenuOpen}>
                {name}
                {disabled ? <Icon icon="crown" className="text-icoBlue" /> : null}
            </TransitionWrapper>
        </>
    );
}
